import { useRouter } from 'vue-router';
import Terminal from '../utils/terminal';
import queryString from 'query-string';
import { showLoadingToast } from 'vant/es';

interface ObjectString {
  [key: string]: string;
}

/**
 * 生产uuid-v4
 */
export function uuidV4() {
  let ret = '',
    value;
  for (let i = 0; i < 32; i++) {
    value = crypto.getRandomValues(new Uint8Array(1))[0] % 16;
    // Insert the hypens
    if (i > 4 && i < 21 && !(i % 4)) {
      ret += '-';
    }
    // Add the next random character
    ret += (i === 12 ? 4 : i === 16 ? (value & 3) | 8 : value).toString(16);
  }
  return ret;
}

//getUUID 没有则生成并存到缓存，缓存有则从缓存取
export function getUUID() {
  let uuid = localStorage.getItem('uuid');
  if (!uuid) {
    uuid = uuidV4();
    localStorage.setItem('uuid', uuid);
  }
  return uuid;
}

// 解析URL中的参数
export function parseUrl(locationSearch?: string): ObjectString {
  if (locationSearch == null || locationSearch == undefined) {
    const url = location.href;
    const urlParams = url.split('?')[1];

    // 不做解码处理
    const parsed = queryString.parse(urlParams, { decode: false });
    // console.log('parsed', parsed);
    return parsed as ObjectString;
  } else {
    const urlParams = locationSearch || location.href;
    // 不做解码处理
    const parsed = queryString.parse(urlParams, { decode: false });
    // console.log('parsed', parsed);
    return parsed as ObjectString;
  }
}

// 传递之前解码，防止VueRouter二次编码
export const decodeURIParams = (target: ObjectString): ObjectString => {
  if (!target || Object.keys(target).length == 0) {
    return {};
  }
  const decodedParams = {};
  for (const attr in target) {
    decodedParams[attr] = decodeURIComponent(target[attr] as string);
  }
  return decodedParams;
};

/**
 * 获取当前运行环境
 * @returns string
 */
export function getEnv(): string {
  if (window['__wxjs_environment'] === 'miniprogram') {
    return 'miniprogram';
  }

  if (Terminal.isSfExpressApp) {
    return 'app';
  }
  // todo 后续添加多个环境的判断条件，公众号，app等
  return 'browser';
}

export function getSystemCode(): string {
  const url = location.href;
  // 总裁直通车系统编码是 ZCZTC
  if (url.indexOf('president') > -1) {
    return 'ZCZTC';
  } else if (url.indexOf('ESG-OCS2-ODP') > -1) {
    return 'ESG-OCS2-ODP';
  } else if (url.indexOf('XGZP') > -1) {
    return 'XGZP';
  } else if (url.indexOf('satisfactionEnter') > -1) {
    // 服务评价
    return 'MCS-AMIS-CORE';
  }
  switch (getEnv()) {
    case 'miniprogram':
      return 'CX-SYSTEM';
    default:
      return 'CX-SYSTEM';
  }
}

// 返回
export const onPopState = () => {
  const envVal = getEnv();
  const router = useRouter();
  if (envVal === 'app') {
    // 返回到列表页
    import('syttunnelsf').then((res) => {
      res.appNavigator.navigate({ routeName: 'ExpressList' });
    });
  } else if (envVal === 'miniprogram') {
    window.history.pushState(null, '', document.URL);
    //返回到最上层
    wx.miniProgram.navigateBack({ delta: 999 });
  } else {
    router.push({ path: '/claimCentre' });
  }
};

export const isGsp = location.hostname.indexOf('gsp') > -1;

export const isSit = location.hostname.indexOf('sit') > -1;

export const isLocal = location.hostname.indexOf('localhost') > -1 || location.hostname.indexOf('127.0.0.1') > -1;

export const getFileExt = (filename: string) => {
  const lastIndex = filename.lastIndexOf('.');
  // 如果存在点，且不是在字符串的第一个位置，返回后缀名
  if (lastIndex !== -1 && lastIndex !== 0) {
    const ext = filename.substring(lastIndex + 1).toLowerCase();
    const fname = filename.substring(0, lastIndex);
    return { filename: fname, ext };
  }
  // 否则没有后缀名，返回空字符串
  return { filename: '', ext: '' };
};

export const getUrlKey = (name, url) => {
  return (
    decodeURIComponent(
      (new RegExp('[?|&]' + name + '=' + '([^&;]+?)(&|#|;|$)').exec(url) || [, ''])[1].replace(/\+/g, '%20'),
    ) || null
  );
};

export const getContentType = (filename: string) => {
  const defaultContentType = 'application/octet-stream';
  const { ext } = getFileExt(filename);

  const contentTypeMap = {
    png: 'image/png',
    bmp: 'image/bmp',
    gif: 'image/gif',
    jpg: 'image/jpeg',
    mp4: 'video/mp4',
    avi: 'video/avi',
    ogg: 'video/ogg',
    webm: 'video/webm',
    mov: 'video/mov',
  };

  return contentTypeMap[ext] ?? defaultContentType;
};

export const validateGoodsName = (goodsName) => {
  if (!goodsName) {
    return false;
  }
  // eslint-disable-next-line no-useless-escape
  const regex = /^(?=.*[\u4E00-\u9FA5a-zA-Z])[\u4E00-\u9FA5a-zA-Z0-9!@#\$%\^\&*\)\(+=._-\W]+$/;
  return regex.test(goodsName);
};

export const setCookie = (cname, cvalue, time = 6) => {
  const d = new Date();
  d.setTime(d.getTime() + time * 24 * 60 * 60 * 1000);
  // @ts-ignore
  const expires = `expires=${d.toGMTString()}`;
  document.cookie = `${cname}=${encodeURIComponent(cvalue)};${expires}`;
};

export const getCookie = (cname) => {
  const name = `${cname}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    const c = ca[i].trim();
    if (c.indexOf(name) === 0) {
      return decodeURIComponent(c.substring(name.length, c.length));
    }
  }
  return '';
};

/**
 * 获取设备平台类型
 * 判断当前设备是Android, iOS还是其他平台
 * @returns 'android' | 'ios' | 'other'
 */
export function getDevicePlatform(): 'android' | 'ios' | 'other' {
  const userAgent = navigator.userAgent.toLowerCase();

  // 检查是否为Android设备
  if (userAgent.indexOf('android') > -1) {
    return 'android';
  }

  // 检查是否为iOS设备 (iPhone, iPad, iPod)
  if (/iphone|ipad|ipod/.test(userAgent) || (userAgent.includes('mac') && 'ontouchend' in document)) {
    return 'ios';
  }

  // 其他平台
  return 'other';
}

// 为iOS和Android原生接口声明类型
declare global {
  interface Window {
    webkit?: {
      messageHandlers: {
        toHomeHandler: {
          postMessage: (message: string) => void;
        };
      };
    };
    android?: {
      postMessage: (message: string) => void;
    };
  }
}

/**
 * 根据不同平台跳转到首页
 * iOS和Android调用不同的原生方法
 * @returns void
 */
export function navigateToHome(): void {
  const platform = getDevicePlatform();
  console.log('platform', platform);
  if (platform === 'ios') {
    // iOS平台调用方法
    // showLoadingToast({
    //   message: '加载中...' + JSON.stringify(window.webkit),
    //   forbidClick: false,
    //   duration: 0,
    // });
    if (window?.webkit && window.webkit?.messageHandlers?.toHomeHandler) {
      try {
        console.log('window.webkit', window.webkit);
        // @ts-ignore
        window.webkit?.messageHandlers?.toHomeHandler.postMessage({});
      } catch (e) {
        console.log('iOS跳转首页失败:', e);
        // 失败刷新页面,防止页面返回按钮失效
        window.location.reload();
      }
    } else {
      // 失败刷新页面,防止页面返回按钮失效
      window.location.reload();
    }
  } else if (platform === 'android') {
    // Android平台调用方法
    console.log('window.android', window.android);
    if (window?.android && window.android?.postMessage) {
      try {
        window.android?.postMessage(
          JSON.stringify({
            id: 'close-page',
            value: '',
          }),
        );
      } catch (e) {
        console.log('Android跳转首页失败:', e);
        // 失败刷新页面,防止页面返回按钮失效
        window.location.reload();
      }
    } else {
      window.location.reload();
    }
  } else {
    // 其他平台的处理（可根据需要修改）
    console.log('当前平台不支持此操作');
  }
}
